import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  Clock, 
  CreditCard, 
  Calendar,
  User,
  DollarSign,
  FileText,
  AlertCircle,
  Wallet,
  Banknote,
  Receipt,
  ExternalLink
} from 'lucide-react';
import { Pagamento } from '../types/Pagamento';
import { User as UserType } from '../types/User';
import { firebaseService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';

interface PagamentoModalProps {
  pagamentoId: string | null;
  alunoId: string | null;
  periodo: string | null; // formato: YYYY-MM
  onClose: () => void;
}

const PagamentoModal: React.FC<PagamentoModalProps> = ({ pagamentoId, alunoId, periodo, onClose }) => {
  const { userProfile } = useAuth();
  const [pagamento, setPagamento] = useState<Pagamento | null>(null);
  const [aluno, setAluno] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [valorPago, setValorPago] = useState<number>(0);
  const [tipoPagamento, setTipoPagamento] = useState<string>('');
  const [observacoes, setObservacoes] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!pagamentoId || !alunoId || !periodo || !userProfile?.empresaCnpj) {
        setLoading(false);
        return;
      }

      try {
        // Buscar dados do pagamento
        const pagamentos = await firebaseService.getPagamentos(userProfile.empresaCnpj);
        const pagamentoEncontrado = pagamentos.find((p: any) => 
          p.id === pagamentoId && 
          p.alunoId === alunoId
        );
        
        if (pagamentoEncontrado) {
          setPagamento(pagamentoEncontrado);
          setValorPago(pagamentoEncontrado.valorPago || pagamentoEncontrado.valor);
          setTipoPagamento(pagamentoEncontrado.tipoPagamento || '');
          setObservacoes(pagamentoEncontrado.observacoes || '');
          
          // Buscar dados do aluno
          const alunosData = await firebaseService.getAlunos(userProfile.empresaCnpj);
          const alunoEncontrado = alunosData.find(a => a.id === alunoId);
          setAluno(alunoEncontrado || null);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar detalhes do pagamento:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [pagamentoId, alunoId, periodo, userProfile]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pago':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Pago</span>;
      case 'cancelado':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><XCircle className="h-3 w-3" /> Cancelado</span>;
      case 'vencido':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Vencido</span>;
      case 'pendente':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Pendente</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'CREDIT_CARD':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"><CreditCard className="h-3 w-3" /> Cartão</span>;
      case 'BOLETO':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800"><Banknote className="h-3 w-3" /> Boleto</span>;
      case 'PIX':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><Wallet className="h-3 w-3" /> PIX</span>;
      case 'Dinheiro':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><Banknote className="h-3 w-3" /> Dinheiro</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{type}</span>;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // Verificar se é formato ISO ou DD/MM/YYYY
    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const [day, month, year] = dateString.split('/');
      date = new Date(`${year}-${month}-${day}`);
    }
    
    return date.toLocaleDateString('pt-BR');
  };

  const handleSalvarPagamento = async () => {
    if (!pagamento || !userProfile?.empresaId) return;
    
    try {
      await firebaseService.updatePagamentoStatus(
        userProfile.empresaCnpj,
        pagamento,
        'pago',
        valorPago
      );
      
      alert('Pagamento registrado com sucesso!');
      setEditMode(false);
      
      // Atualizar o pagamento na interface
      setPagamento({
        ...pagamento,
        status: 'pago',
        valorPago,
        dataPagamento: new Date().toISOString(),
        tipoPagamento
      });
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      alert('Erro ao registrar pagamento. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!pagamento) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Pagamento não encontrado</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex items-center justify-center p-8">
            <AlertCircle className="h-12 w-12 text-red-500 mr-3" />
            <p className="text-gray-700">Não foi possível encontrar os detalhes deste pagamento.</p>
          </div>
          <div className="mt-6 flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Detalhes do Pagamento</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Status e Valor */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Receipt className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Pagamento {pagamento.codigo}</h3>
                  <p className="text-sm text-gray-500">ID: {pagamento.id}</p>
                </div>
              </div>
              <div className="flex items-center gap-x-3">
                {getStatusBadge(pagamento.status)}
                <span className="text-xl font-bold text-gray-900">{formatCurrency(pagamento.valor)}</span>
              </div>
            </div>

            {/* Informações do Aluno */}
            <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center gap-x-3 mb-3">
                <User className="h-5 w-5 text-gray-500" />
                <h3 className="text-md font-medium text-gray-900">Informações do Aluno</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nome</p>
                  <p className="text-sm font-medium text-gray-900">{aluno?.nome || 'Não disponível'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ID do Aluno</p>
                  <p className="text-sm font-medium text-gray-900">{pagamento.alunoId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{aluno?.email || 'Não disponível'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefone</p>
                  <p className="text-sm font-medium text-gray-900">{aluno?.telefone || 'Não disponível'}</p>
                </div>
              </div>
            </div>

            {/* Detalhes do Pagamento */}
            <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center gap-x-3 mb-3">
                <FileText className="h-5 w-5 text-gray-500" />
                <h3 className="text-md font-medium text-gray-900">Detalhes do Pagamento</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Forma de Pagamento</p>
                  <div className="mt-1">
                    {editMode ? (
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={tipoPagamento}
                        onChange={(e) => setTipoPagamento(e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        <option value="CREDIT_CARD">Cartão de Crédito</option>
                        <option value="BOLETO">Boleto</option>
                        <option value="PIX">PIX</option>
                        <option value="Dinheiro">Dinheiro</option>
                      </select>
                    ) : (
                      pagamento.tipoPagamento ? getPaymentTypeBadge(pagamento.tipoPagamento) : '-'
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor</p>
                  <div className="flex items-center gap-x-1 mt-1">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(pagamento.valor)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor Pago</p>
                  <div className="flex items-center gap-x-1 mt-1">
                    {editMode ? (
                      <input
                        type="number"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={valorPago}
                        onChange={(e) => setValorPago(Number(e.target.value))}
                        step="0.01"
                        min="0"
                      />
                    ) : (
                      <>
                        <DollarSign className="h-4 w-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-900">
                          {pagamento.valorPago ? formatCurrency(pagamento.valorPago) : '-'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data de Vencimento</p>
                  <div className="flex items-center gap-x-1 mt-1">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <p className="text-sm font-medium text-gray-900">{formatDate(pagamento.dataVencimento)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data de Pagamento</p>
                  <div className="flex items-center gap-x-1 mt-1">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <p className="text-sm font-medium text-gray-900">
                      {pagamento.dataPagamento ? formatDate(pagamento.dataPagamento) : '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Período</p>
                  <div className="flex items-center gap-x-1 mt-1">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(pagamento.dataInicio)} a {formatDate(pagamento.dataFim)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Observações */}
            <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center gap-x-3 mb-3">
                <FileText className="h-5 w-5 text-gray-500" />
                <h3 className="text-md font-medium text-gray-900">Observações</h3>
              </div>
              {editMode ? (
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  placeholder="Adicione observações sobre este pagamento..."
                />
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-line">
                  {pagamento.observacoes || 'Nenhuma observação registrada.'}
                </p>
              )}
            </div>

            {/* Links de Pagamento */}
            {(pagamento.invoiceUrl || pagamento.bankSlipUrl || pagamento.pixUrl) && (
              <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-x-3 mb-3">
                  <ExternalLink className="h-5 w-5 text-gray-500" />
                  <h3 className="text-md font-medium text-gray-900">Links de Pagamento</h3>
                </div>
                <div className="space-y-2">
                  {pagamento.invoiceUrl && (
                    <div>
                      <a 
                        href={pagamento.invoiceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800 flex items-center gap-x-1"
                      >
                        <ExternalLink className="h-4 w-4" /> Fatura Online
                      </a>
                    </div>
                  )}
                  {pagamento.bankSlipUrl && (
                    <div>
                      <a 
                        href={pagamento.bankSlipUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800 flex items-center gap-x-1"
                      >
                        <ExternalLink className="h-4 w-4" /> Boleto
                      </a>
                    </div>
                  )}
                  {pagamento.pixUrl && (
                    <div>
                      <a 
                        href={pagamento.pixUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-800 flex items-center gap-x-1"
                      >
                        <ExternalLink className="h-4 w-4" /> PIX
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-x-3">
          {editMode ? (
            <>
              <button 
                onClick={() => setEditMode(false)}
                className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSalvarPagamento}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Salvar Pagamento
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={onClose}
                className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
              {pagamento.status !== 'pago' && (
                <button 
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Registrar Pagamento
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagamentoModal;
